import { useLayoutEffect, useMemo, useRef } from "react";

import { getHandleTransitionEnd } from "./functions/getHandleTransitionEnd";
import { isPrefersReducedMotion } from "./functions/isPrefersReducedMotion";
import { setAnimationStyles } from "./functions/setAnimationStyles";
import { setTransitionStyles } from "./functions/setTransitionStyle";

import type { Props } from "./props";

const DURATION = 250;

export const useThis = (input: Props) => {
  const { id, isOpened } = input;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const prefersReduced = useMemo(isPrefersReducedMotion, []);

  useLayoutEffect(() => {
    if (!panelRef.current || !innerRef.current) return;

    const panelElement = panelRef.current;
    const innerElement = innerRef.current;

    const canTransition = !prefersReduced && DURATION > 0;

    setTransitionStyles({ canTransition, duration: DURATION, panelElement });
    panelElement.style.overflow = "hidden";
    panelElement.style.boxSizing = "border-box";
    const handleTransitionEnd = getHandleTransitionEnd({
      isOpened,
      panelElement,
    });

    setAnimationStyles({
      canTransition,
      handleTransitionEnd,
      innerElement,
      isOpened,
      panelElement,
    });
    if (containerRef.current) {
      requestAnimationFrame(() => {
        containerRef.current?.setAttribute("data-collapsible-ready", "1");
      });
    }

    return () => {
      panelElement.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isOpened, prefersReduced]);

  return {
    containerRef,
    innerRef,
    panelId: `${id}-panel`,
    panelRef,
    triggerId: `${id}-btn`,
  };
};
